import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Title,
  Text, 
  Card, 
  Group,
  Badge, 
  Stack, 
  Button,
  Table,
  Progress,
  SimpleGrid,
  ThemeIcon,
  Box,
  TextInput,
  Select,
  Pagination,
  Modal,
  Alert,
  ActionIcon
} from '@mantine/core';
import { useSupabase } from '../contexts/SupabaseContext';
import { useWeb3 } from '../contexts/Web3Context';
import {
  IconLock,
  IconLockOpen,
  IconCalendar,
  IconSearch,
  IconFilter,
  IconPlus,
  IconCheck,
  IconEdit
} from '@tabler/icons-react';

/**
 * 실제 MCP 분석 결과를 바탕으로 생성된 LockupManagement 컴포넌트
 * Figma LockListFrame의 실제 구조와 디자인 토큰을 반영
 */
const LockupManagement = () => {
  const { loadLockups, isConnected: supabaseConnected, error: supabaseError } = useSupabase();
  const [searchValue, setSearchValue] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedLockup, setSelectedLockup] = useState(null);
  const [opened, setOpened] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lockupData, setLockupData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [statisticsData, setStatisticsData] = useState([]);
  const itemsPerPage = 10;

  // Add Lockup modal states
  const [addModalOpened, setAddModalOpened] = useState(false);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [lockAmountEth, setLockAmountEth] = useState('');
  const [lockDays, setLockDays] = useState('');

  // Web3 (for write)
  const { isWriteReady, adminLevel, account, contractWrite } = useWeb3();
  const { supabase } = useSupabase();

  // 통계 데이터 계산
  const calculateStatistics = useCallback((allLockups) => {
    const now = new Date();
    const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    let totalLocked = 0;
    let thisWeekUnlock = 0;
    let next30DaysUnlock = 0;
    let alreadyUnlocked = 0;
    
    allLockups.forEach(lockup => {
      const amount = parseFloat(lockup.amount || 0);
      
      // 모든 락업의 원래 수량을 totalLocked에 포함
      totalLocked += amount;
      
      // 이미 언락된 경우 alreadyUnlocked에 추가
      if (lockup.unlock_yn === 'Y') {
        alreadyUnlocked += amount;
      }
      
      // 이번주 언락 계산 (expire_dt가 이번주 내인 경우)
      if (lockup.expire_dt) {
        const expireDate = new Date(lockup.expire_dt);
        const remainingDays = Math.ceil((expireDate - now) / (1000 * 60 * 60 * 24));
        
        if (expireDate >= now && expireDate <= oneWeekFromNow) {
          // next_unlock_amount가 있으면 사용, 없으면 amount 사용
          const unlockAmount = parseFloat(lockup.next_unlock_amount || lockup.amount || 0);
          thisWeekUnlock += unlockAmount;
          
          // 디버깅 로그
          console.log('이번주 언락 락업:', {
            id: lockup.id,
            expire_dt: lockup.expire_dt,
            remainingDays,
            next_unlock_amount: lockup.next_unlock_amount,
            amount: lockup.amount,
            unlockAmount,
            thisWeekUnlock
          });
        }
        
        // 다음 30일 언락 계산
        if (expireDate >= now && expireDate <= thirtyDaysFromNow) {
          // next_unlock_amount가 있으면 사용, 없으면 amount 사용
          const unlockAmount = parseFloat(lockup.next_unlock_amount || lockup.amount || 0);
          next30DaysUnlock += unlockAmount;
          
          // 디버깅 로그
          console.log('30일 내 언락 락업:', {
            id: lockup.id,
            expire_dt: lockup.expire_dt,
            remainingDays,
            next_unlock_amount: lockup.next_unlock_amount,
            amount: lockup.amount,
            unlockAmount,
            next30DaysUnlock
          });
        }
      }
    });
    
    return [
      { 
        title: 'Total Locked', 
        value: totalLocked.toLocaleString(), 
        unit: 'EDENA',
        icon: IconLock, 
        color: 'purple'
      },
      { 
        title: 'This Week Unlock', 
        value: thisWeekUnlock.toLocaleString(), 
        unit: 'EDENA',
        icon: IconLockOpen, 
        color: 'orange'
      },
      { 
        title: 'Next 30 Days Unlock', 
        value: next30DaysUnlock.toLocaleString(), 
        unit: 'EDENA',
        icon: IconCalendar, 
        color: 'blue'
      },
      { 
        title: 'Already Unlocked (MTD)', 
        value: alreadyUnlocked.toLocaleString(), 
        unit: 'EDENA',
        icon: IconCheck, 
        color: 'green'
      }
    ];
  }, []);

  // Supabase에서 락업 데이터 로드
  const loadLockupData = useCallback(async () => {
    setLoading(true);
    try {
      if (!supabaseConnected) {
        console.log('Supabase not connected, skipping lockup data load');
        setLoading(false);
        return;
      }

      console.log('Supabase를 통해 락업 데이터 로드 중...');
      
      const result = await loadLockups(currentPage, itemsPerPage);
      
      if (result && result.data) {
        const formattedData = result.data.map((lockup) => {
          const amount = parseFloat(lockup.amount || 0);
          
          // expire_dt까지 남은 일수 계산
          let remainingDays = 0;
          if (lockup.expire_dt) {
            const expireDate = new Date(lockup.expire_dt);
            const now = new Date();
            const diffTime = expireDate - now;
            remainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (remainingDays < 0) remainingDays = 0;
          }
          
          // 상태 결정
          let status = 'Scheduled';
          let displayAmount = amount; // 원래 락업 수량을 항상 표시
          let displayRemaining = remainingDays;
          
          if (lockup.unlock_yn === 'Y') {
            status = 'Unlocked';
            displayRemaining = 0; // 이미 언락된 경우 0 표시
          } else if (remainingDays <= 0) {
            status = 'EXPIRED';
            displayRemaining = 0;
          }
          
          return {
            id: lockup.id,
            name: lockup.accounts?.holder_name || 'Anonymous',
            address: lockup.address,
            amount: displayAmount.toLocaleString(),
            remaining: `${displayRemaining} days`,
            expire_dt: lockup.expire_dt ? new Date(lockup.expire_dt).toLocaleDateString() : 'N/A',
            unlocked: lockup.unlock_dt ? new Date(lockup.unlock_dt).toLocaleDateString() : 'N/A',
            status: status,
            progress: lockup.unlock_yn === 'Y' ? 100 : 0
          };
        });
        
        setLockupData(formattedData);
        setTotalCount(result.count || 0);
        
        // 통계 데이터 계산 (전체 데이터 필요)
        const allLockupsResult = await loadLockups(1, 1000); // 전체 데이터 가져오기
        if (allLockupsResult && allLockupsResult.data) {
          const stats = calculateStatistics(allLockupsResult.data);
          setStatisticsData(stats);
        }
        
        console.log('Supabase 락업 데이터 로드 완료');
      } else {
        console.log('No lockup data found');
        setLockupData([]);
        setTotalCount(0);
        setStatisticsData([]);
      }
    } catch (error) {
      console.error('Error loading lockups from Supabase:', error);
      setLockupData([]);
      setTotalCount(0);
      setStatisticsData([]);
    } finally {
      setLoading(false);
    }
  }, [supabaseConnected, loadLockups, currentPage, itemsPerPage, calculateStatistics]);

  // 페이지 변경 시 데이터 로드
  const handlePageChange = useCallback(async (page) => {
    setCurrentPage(page);
    await loadLockupData();
  }, [loadLockupData]);

  // Supabase 연결 시 자동 로딩
  useEffect(() => {
    if (supabaseConnected && lockupData.length === 0 && !loading) {
      console.log('Auto-loading lockups from Supabase...');
      loadLockupData();
    }
  }, [supabaseConnected, loadLockupData, loading, lockupData]);

  // 페이지 변경 시 데이터 로드
  useEffect(() => {
    if (currentPage > 1) {
      loadLockupData();
    }
  }, [currentPage, loadLockupData]);

  // MCP가 추천한 필터 옵션
  const filterOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'unlocked', label: 'Unlocked' },
    { value: 'expired', label: 'Expired' }
  ];

  // 실제 디자인에 맞춘 컬럼 구조
  const columns = [
    { key: 'name', label: 'Holder', sortable: true },
    { key: 'address', label: 'Wallet Address', sortable: true },
    { key: 'amount', label: 'Total Locked', sortable: true },
    { key: 'remaining', label: 'Remaining Days', sortable: true },
    { key: 'expire_dt', label: 'Expire Date', sortable: true },
    { key: 'unlocked', label: 'Unlocked Date', sortable: true },
    { key: 'status', label: 'Status', sortable: false },
    { key: 'actions', label: 'Actions', sortable: false }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Progress': return 'blue';
      case 'Scheduled': return 'orange';
      case 'Unlocked': return 'green';
      case 'EXPIRED': return 'red';
      default: return 'gray';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'In Progress': return '#1976d2';
      case 'Scheduled': return '#ed6c02';
      case 'Unlocked': return '#2e7d32';
      case 'EXPIRED': return '#d32f2f';
      default: return '#9e9e9e';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'In Progress': return '#ffffff';
      case 'Scheduled': return '#ffffff';
      case 'Unlocked': return '#ffffff';
      case 'EXPIRED': return '#ffffff';
      default: return '#000000';
    }
  };

  // 검색 필터링된 락업들
  const filteredLockups = lockupData.filter(lockup => {
    const matchesSearch = lockup.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                          lockup.address.toLowerCase().includes(searchValue.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'in-progress' && lockup.status === 'In Progress') ||
                         (filterStatus === 'scheduled' && lockup.status === 'Scheduled') ||
                         (filterStatus === 'unlocked' && lockup.status === 'Unlocked') ||
                         (filterStatus === 'expired' && lockup.status === 'Expired');
    return matchesSearch && matchesFilter;
  });

  return (
    <Container fluid py="md" pl="xl">
      {/* MCP가 분석한 헤더 섹션 */}
      <Box mb="xl">
        <div>
          <Title order={2}>Lockup List</Title>
          <Text size="sm" c="dimmed" mt={4}>
          Monitor token lockup schedules and unlocks
          </Text>
        </div>

        {/* MCP가 추천한 검색 및 필터 */}
        <Group mb="md">
          <TextInput
            placeholder="Search by name or address..."
            leftSection={<IconSearch size={16} />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by status"
            leftSection={<IconFilter size={16} />}
            data={filterOptions}
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: 150 }}
          />
          {/* <Button leftSection={<IconDownload size={16} />} variant="outline">
            Export
          </Button> */}
          <Button 
            leftSection={<IconPlus size={16} />} 
            onClick={() => {
              if (adminLevel !== 'Owner') {
                alert('Owner 만 가능한 기능입니다');
                return;
              }
              setRecipientAddress('');
              setLockAmountEth('');
              setLockDays('');
              setAddModalOpened(true);
            }}
          >
            Add Lockup
          </Button>
        </Group>
      </Box>

      {/* View Toggle */}
      {/* <Group justify="flex-end" mb="md">
        <Button.Group>
          <Button variant="filled" size="sm">Table View</Button>
          <Button variant="outline" size="sm">Calendar View</Button>
        </Button.Group>
      </Group> */}

      {/* MCP가 분석한 통계 카드 */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} mb="xl">
        {statisticsData.length > 0 ? statisticsData.map((stat, index) => (
          <Card key={index} withBorder shadow="sm" p="md" radius="md">
            <Group justify="space-between" mb="md">
              <Box>
                <Text size="sm" c="dimmed" mb={4}>
                  {stat.title}
                </Text>
                <Text size="xl" fw={700} mb={2}>
                  {stat.value}
                </Text>
                <Text size="sm" c="dimmed">
                  {stat.unit}
                </Text>
              </Box>
              <ThemeIcon color={stat.color} variant="light" size="lg" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
                <stat.icon size={20} />
              </ThemeIcon>
            </Group>
          </Card>
        )) : (
          // 로딩 상태
          Array.from({ length: 4 }).map((_, index) => (
            <Card key={index} withBorder shadow="sm" p="md" radius="md">
              <Group justify="space-between" mb="md">
                <Box>
                  <Text size="sm" c="dimmed" mb={4}>
                    Loading...
                  </Text>
                  <Text size="xl" fw={700} mb={2}>
                    ---
                  </Text>
                  <Text size="sm" c="dimmed">
                    EDENA
                  </Text>
                </Box>
                <ThemeIcon color="gray" variant="light" size="lg" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
                  <IconLock size={20} />
                </ThemeIcon>
              </Group>
            </Card>
          ))
        )}
      </SimpleGrid>

      {/* Lockup Details 섹션 */}
      <Box mb="md">
        <Title order={3} mb="xs">Lockup Details</Title>
        <Text c="dimmed" mb="md">Detailed view of all token lockups</Text>
      </Box>

      {/* MCP가 분석한 데이터 테이블 */}
      <Card withBorder shadow="sm" p="md">
        {supabaseError && (
          <Alert color="red" mb="md">
            Database 연결 오류: {supabaseError}. 데이터를 불러올 수 없습니다.
          </Alert>
        )}

        <Group justify="space-between" mb="md">
          <Title order={3}>Lockup Records</Title>
          <Text size="sm" c="dimmed">Total {totalCount} records</Text>
        </Group>
        
        <Table>
          <Table.Thead>
            <Table.Tr>
              {columns.map((column) => (
                <Table.Th key={column.key}>
                  {column.label}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  <Text>Loading lockups...</Text>
                </Table.Td>
              </Table.Tr>
            ) : filteredLockups.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  <Text>No lockups found</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filteredLockups.map((lockup) => (
              <Table.Tr key={lockup.id}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {lockup.name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" style={{ fontFamily: 'monospace' }}>
                    {lockup.address}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {lockup.amount} EDENA
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {lockup.remaining}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {lockup.expire_dt}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {lockup.unlocked}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={getStatusColor(lockup.status)} variant="filled" size="sm"
                        style={{ 
                          backgroundColor: getStatusBgColor(lockup.status),
                          color: getStatusTextColor(lockup.status)
                        }}
                  >
                    {lockup.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    color="gray"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedLockup(lockup);
                      setOpened(true);
                    }}
                  >
                    <IconEdit size="1rem" />
                  </ActionIcon>

                </Table.Td>
              </Table.Tr>
              ))
            )}
          </Table.Tbody>
                </Table>

        {/* MCP가 추천한 페이지네이션 */}
        {Math.ceil(totalCount / itemsPerPage) > 1 && (
          <Group justify="center" mt="md">
            <Pagination
              value={currentPage}
              onChange={handlePageChange}
              total={Math.ceil(totalCount / itemsPerPage)}
              size="sm"
            />
          </Group>
        )}
      </Card>

      {/* Add Lockup Modal */}
      <Modal
        opened={addModalOpened}
        onClose={() => setAddModalOpened(false)}
        title="Add Lockup"
        size="md"
      >
        <Stack>
          <TextInput
            label="Recipient Address"
            placeholder="0x..."
            value={recipientAddress}
            onChange={(e) => setRecipientAddress(e.target.value)}
            required
          />
          <TextInput
            label="Lock Amount (tokens)"
            placeholder="정수 토큰 수량 (예: 100)"
            value={lockAmountEth}
            onChange={(e) => setLockAmountEth(e.target.value)}
            required
          />
          <TextInput
            label="Lockup Days"
            placeholder="예: 90"
            value={lockDays}
            onChange={(e) => setLockDays(e.target.value)}
            required
          />
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={() => setAddModalOpened(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                try {
                  if (adminLevel !== 'Owner') {
                    alert('Owner 만 가능한 기능입니다');
                    return;
                  }
                  if (!isWriteReady) {
                    alert('지갑 연결 및 네트워크를 확인해주세요.');
                    return;
                  }

                  const addr = (recipientAddress || '').trim();
                  const amountStr = (lockAmountEth || '').trim();
                  const daysStr = (lockDays || '').trim();

                  if (!addr || !addr.startsWith('0x') || addr.length !== 42) {
                    alert('유효한 수신 주소를 입력하세요.');
                    return;
                  }

                  const amountNum = Number(amountStr);
                  const daysNum = Number(daysStr);
                  if (!Number.isFinite(amountNum) || amountNum <= 0 || !Number.isInteger(amountNum)) {
                    alert('락업 수량은 0보다 큰 정수여야 합니다. (컨트랙트가 10^18 배를 내부 처리)');
                    return;
                  }
                  if (!Number.isFinite(daysNum) || daysNum <= 0 || !Number.isInteger(daysNum)) {
                    alert('락업 일수는 0보다 큰 정수여야 합니다.');
                    return;
                  }

                  // estimate gas and send
                  const gas = await contractWrite.methods
                    .transferWithLockEasy(addr, String(amountNum), String(daysNum))
                    .estimateGas({ from: account })
                    .catch(() => undefined);

                  await contractWrite.methods
                    .transferWithLockEasy(addr, String(amountNum), String(daysNum))
                    .send({ from: account, gas });

                  // Supabase에 lockup 기록 추가
                  try {
                    const releaseDate = new Date(Date.now() + daysNum * 24 * 60 * 60 * 1000);

                    // accounts.id 조회 (옵션)
                    let accountId = null;
                    try {
                      const { data: accRow } = await supabase
                        .from('accounts')
                        .select('id')
                        .eq('address', addr)
                        .single();
                      accountId = accRow?.id || null;
                    } catch (_) {}

                    const { error: insertErr } = await supabase
                      .from('lockup')
                      .insert([
                        {
                          account_id: accountId,
                          address: addr,
                          amount: amountNum,
                          expire_dt: releaseDate.toISOString(),
                          expire_yn: 'N',
                          unlock_dt: null,
                          unlock_yn: 'N',
                          updated_at: new Date().toISOString(),
                        },
                      ]);
                    if (insertErr) {
                      console.error('lockup 등록 실패:', insertErr.message);
                    }
                  } catch (dbErr) {
                    console.error('lockup DB 작업 중 오류:', dbErr);
                  }

                  alert('락업 전송이 완료되었습니다.');
                  setAddModalOpened(false);
                  await loadLockupData();
                } catch (err) {
                  console.error('Add lockup failed:', err);
                  alert(`락업 전송 중 오류가 발생했습니다: ${err?.message || err}`);
                }
              }}
            >
              Submit
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* MCP가 추천한 상세 모달 */}
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Lockup Details"
        size="md"
      >
        {selectedLockup && (
          <Stack gap="md">
            <Group>
              <Text fw={500}>Name:</Text>
              <Text>{selectedLockup.name}</Text>
            </Group>
            <Group>
              <Text fw={500}>Address:</Text>
              <Text style={{ fontFamily: 'monospace' }}>{selectedLockup.address}</Text>
            </Group>
            <Group>
              <Text fw={500}>Amount:</Text>
              <Text>{selectedLockup.amount} EDENA</Text>
            </Group>
            <Group>
              <Text fw={500}>Remaining:</Text>
              <Text>{selectedLockup.remaining} EDENA</Text>
            </Group>
            <Group>
              <Text fw={500}>Expire Date:</Text>
              <Text>{selectedLockup.expire_dt}</Text>
            </Group>
            <Group>
              <Text fw={500}>Status:</Text>
              <Badge 
                color={getStatusColor(selectedLockup.status)}
                variant="filled"
                style={{ 
                  backgroundColor: getStatusBgColor(selectedLockup.status),
                  color: getStatusTextColor(selectedLockup.status)
                }}
              >
                {selectedLockup.status}
              </Badge>
            </Group>
            <Group>
              <Text fw={500}>Progress:</Text>
              <Progress 
                value={selectedLockup.progress} 
                color={selectedLockup.progress === 100 ? 'green' : 'blue'}
                style={{ width: 200 }}
              />
          </Group>
        </Stack>
        )}
      </Modal>
    </Container>
  );
};

export default LockupManagement;